import { supabase } from '../../lib/supabase'
import { CONFIG } from '../../config/config'

export const goalSlice = (set, get) => ({
  goals:  [],
  phases: [],
  tasks:  [],
  loadingGoals: false,

  loadAll: async () => {
    const uid = get().user?.id
    if (!uid) return
    set({ loadingGoals: true })

    const [g, p, t] = await Promise.all([
      supabase.from('goals').select('*').eq('user_id', uid).order('created_at'),
      supabase.from('phases').select('*').eq('user_id', uid).order('order_index'),
      supabase.from('tasks').select('*').eq('user_id', uid).order('created_at'),
    ])

    set({ goals: g.data ?? [], phases: p.data ?? [], tasks: t.data ?? [], loadingGoals: false })
  },

  // ── Goals ──────────────────────────────────────────────────────────────────
  createGoal: async (payload) => {
    const uid     = get().user?.id
    const goals   = get().goals
    const color   = CONFIG.GOAL_COLORS[goals.length % CONFIG.GOAL_COLORS.length]
    const isCommit = payload.bucket === 'committed'

    const row = {
      user_id:         uid,
      title:           payload.title,
      description:     payload.description ?? '',
      category:        payload.category ?? '',
      bucket:          payload.bucket ?? 'aspirational',
      status:          isCommit ? 'committed' : 'aspirational',
      effort_level:    payload.effort_level ?? 1,
      estimated_weeks: payload.estimated_weeks ?? null,
      weekly_hours:    payload.weekly_hours ?? null,
      color,
      created_at:      new Date().toISOString(),
      updated_at:      new Date().toISOString(),
    }

    const { data, error } = await supabase.from('goals').insert(row).select().single()
    if (error) return { error }
    set(s => ({ goals: [...s.goals, data] }))
    return { data }
  },

  updateGoal: async (id, updates) => {
    const { data, error } = await supabase
      .from('goals')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id).select().single()
    if (!error) set(s => ({ goals: s.goals.map(g => g.id === id ? data : g) }))
    return { data, error }
  },

  deleteGoal: async (id) => {
    const { error } = await supabase.from('goals').delete().eq('id', id)
    if (!error) set(s => ({
      goals:  s.goals.filter(g => g.id !== id),
      phases: s.phases.filter(p => p.goal_id !== id),
      tasks:  s.tasks.filter(t => t.goal_id !== id),
    }))
    return { error }
  },

  // Status transitions
  activateGoal: async (id) => {
    const { goals, profile } = get()
    const cap = profile?.active_goal_cap ?? 3
    if (goals.filter(g => g.status === 'active').length >= cap)
      return { error: 'cap_reached' }
    return get().updateGoal(id, { status: 'active', bucket: 'committed', started_at: new Date().toISOString() })
  },
  pauseGoal:    (id) => get().updateGoal(id, { status: 'paused',    paused_at: new Date().toISOString() }),
  resumeGoal:   (id) => get().updateGoal(id, { status: 'active',    paused_at: null }),
  completeGoal: (id) => get().updateGoal(id, { status: 'completed', completed_at: new Date().toISOString() }),
  archiveGoal:  (id) => get().updateGoal(id, { status: 'archived',  archived_at: new Date().toISOString() }),
  backlogGoal:  (id) => get().updateGoal(id, { status: 'aspirational', bucket: 'aspirational', started_at: null }),

  // ── Phases ─────────────────────────────────────────────────────────────────
  createPhase: async (goalId, payload) => {
    const uid     = get().user?.id
    const existing = get().phases.filter(p => p.goal_id === goalId)
    const row = {
      user_id:         uid,
      goal_id:         goalId,
      title:           payload.title,
      order_index:     payload.order_index ?? existing.length,
      status:          'pending',
      estimated_weeks: payload.estimated_weeks ?? null,
      effort_level:    payload.effort_level ?? null,
      created_at:      new Date().toISOString(),
    }
    const { data, error } = await supabase.from('phases').insert(row).select().single()
    if (!error) set(s => ({ phases: [...s.phases, data] }))
    return { data, error }
  },

  updatePhase: async (id, updates) => {
    const { data, error } = await supabase.from('phases').update(updates).eq('id', id).select().single()
    if (!error) set(s => ({ phases: s.phases.map(p => p.id === id ? data : p) }))
    return { data, error }
  },

  deletePhase: async (id) => {
    const { error } = await supabase.from('phases').delete().eq('id', id)
    if (!error) set(s => ({
      phases: s.phases.filter(p => p.id !== id),
      tasks:  s.tasks.filter(t => t.phase_id !== id),
    }))
    return { error }
  },

  completePhase: async (id, actualWeeks) =>
    get().updatePhase(id, { status: 'completed', actual_weeks: actualWeeks, completed_at: new Date().toISOString() }),

  // ── Tasks ──────────────────────────────────────────────────────────────────
  createTask: async (phaseId, goalId, payload) => {
    const uid = get().user?.id
    const row = {
      user_id: uid, phase_id: phaseId, goal_id: goalId,
      title: payload.title, status: 'pending',
      estimated_hours: payload.estimated_hours ?? null,
      created_at: new Date().toISOString(),
    }
    const { data, error } = await supabase.from('tasks').insert(row).select().single()
    if (!error) set(s => ({ tasks: [...s.tasks, data] }))
    return { data, error }
  },

  toggleTask: async (id) => {
    const task = get().tasks.find(t => t.id === id)
    if (!task) return
    const done = task.status !== 'completed'
    const { data, error } = await supabase.from('tasks')
      .update({ status: done ? 'completed' : 'pending', completed_at: done ? new Date().toISOString() : null })
      .eq('id', id).select().single()
    if (!error) set(s => ({ tasks: s.tasks.map(t => t.id === id ? data : t) }))
    return { data, error }
  },

  deleteTask: async (id) => {
    const { error } = await supabase.from('tasks').delete().eq('id', id)
    if (!error) set(s => ({ tasks: s.tasks.filter(t => t.id !== id) }))
    return { error }
  },

  // ── Selectors ──────────────────────────────────────────────────────────────
  activeGoals:       () => get().goals.filter(g => g.status === 'active'),
  committedGoals:    () => get().goals.filter(g => ['committed','active','paused'].includes(g.status)),
  aspirationalGoals: () => get().goals.filter(g => g.bucket === 'aspirational' && !['archived','completed'].includes(g.status)),
  goalPhases:        (gid) => get().phases.filter(p => p.goal_id === gid).sort((a,b) => a.order_index - b.order_index),
  phaseTasks:        (pid) => get().tasks.filter(t => t.phase_id === pid),
})
